import React, { useMemo, useState } from 'react';

import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';

import { RatePlanOption, useBundleRatePlanOptions } from '~/hooks/apis/accommodation/useBundleRatePlanOptions';

import LinkableChip from './LinkableChip';

interface Props {
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  /** svc-accommodation property UUIDs attached to this bundle. Used to scope which rate plans
   * the dropdown shows. Empty array => picker is in "paste-only" mode (no dropdown options). */
  propertyIds: Array<string>;
  label?: string;
  placeholder?: string;
  /** When true, the picker is read-only and greyed out. Use when an `allow all` override is on
   * elsewhere in the form (the allowlist is irrelevant in that case). */
  disabled?: boolean;
}

// Loose UUID v1–v5 regex — server enforces strictly via Strummer.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isValidUuid = (s: string): boolean => UUID_REGEX.test(s);

// Split free-form text on common separators (comma, semicolon, whitespace) so users can paste
// lists copied from spreadsheets/chats and have them turn into chips automatically.
const splitInputIntoIds = (raw: string): Array<string> =>
  raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

function BundleLpcRatePlanIdsPicker({
  value,
  onChange,
  propertyIds,
  label = 'LPC rate plan IDs (optional)',
  placeholder = 'Search rate plans by name, or paste UUID(s)',
  disabled = false,
}: Props) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Lazy-fetch trigger: only resolve vendors + rate plans when the user has interacted with the
  // field (focus/open) OR when there are existing chips to render names for. Keeps page-load cost flat.
  const enabled = open || hasInteracted || value.length > 0;

  const {
    ratePlansById,
    vendorIdByRatePlanId,
    isLoading,
    isError: isAnyError,
  } = useBundleRatePlanOptions(propertyIds, enabled);

  const invalid = useMemo(() => value.filter((id) => !isValidUuid(id)), [value]);

  // Dropdown options: all known rate plans, minus already-selected. Sort by name for stability.
  const options: Array<RatePlanOption> = useMemo(() => {
    const selected = new Set(value);
    return [...ratePlansById.values()].filter((p) => !selected.has(p.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [ratePlansById, value]);

  const addIds = (ids: Array<string>) => {
    if (ids.length === 0) return;
    const merged = [...value];
    for (const id of ids) {
      if (!merged.includes(id)) merged.push(id);
    }
    onChange(merged);
  };

  const helperText = (() => {
    if (invalid.length > 0) {
      return `Invalid UUIDs (server will reject): ${invalid.join(', ')}`;
    }
    if (propertyIds.length === 0) {
      return 'Attach properties to the bundle first to browse their rate plans. You can still paste UUIDs manually.';
    }
    if (isAnyError) {
      return 'Could not load some rate plans — type or paste UUIDs manually. Server will validate on save.';
    }
    return 'Leave empty to allow all LPC rates. Bedbank rates are never restricted by this list.';
  })();

  const noOptionsText = (() => {
    if (isLoading) return 'Loading rate plans…';
    if (propertyIds.length === 0) return 'No properties on this bundle yet.';
    if (isAnyError && ratePlansById.size === 0) {
      return 'Could not load rate plans. Paste a UUID to add manually.';
    }
    if (ratePlansById.size === 0) return 'No rate plans available for these properties.';
    return 'No matching rate plans.';
  })();

  return (
    <Autocomplete<string, true, false, true>
      multiple
      freeSolo
      disabled={disabled}
      open={open && !disabled}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options.map((o) => o.id)}
      value={value}
      inputValue={inputValue}
      loading={isLoading}
      onChange={(_event, next, reason) => {
        const flattened = next.flatMap((entry) => (typeof entry === 'string' ? splitInputIntoIds(entry) : [entry]));
        const deduped: Array<string> = [];
        for (const id of flattened) {
          if (!deduped.includes(id)) deduped.push(id);
        }
        onChange(deduped);
        if (reason === 'createOption' || reason === 'selectOption') setInputValue('');
      }}
      onInputChange={(_event, next, reason) => {
        if (reason === 'reset') return;
        // Detect a separator-terminated paste/type and turn it into chips so admins can paste
        // "id1, id2, id3 " and see three chips immediately.
        const trailing = /[\s,;]$/.test(next);
        if (trailing) {
          const ids = splitInputIntoIds(next);
          if (ids.length > 0) {
            addIds(ids);
            setInputValue('');
            return;
          }
        }
        setInputValue(next);
      }}
      // Filter by name OR UUID substring so admins can type a partial name OR paste a partial UUID.
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        if (!q) return opts;
        return opts.filter((id) => {
          if (id.toLowerCase().includes(q)) return true;
          const name = ratePlansById.get(id)?.name?.toLowerCase() ?? '';
          return name.includes(q);
        });
      }}
      renderTags={(items, getTagProps) =>
        items.map((id, index) => {
          const known = ratePlansById.get(id);
          const valid = isValidUuid(id);
          // `vendorId` is the Salesforce account id (id_salesforce_external). The admin route
          // `/vendors/:id_vendor/rate-plans/:id_rate_plan` deep-links straight to the rate plan
          // detail page, which is more useful for debugging than the vendor root.
          const vendorId = vendorIdByRatePlanId.get(id);
          const href = vendorId ? `/vendors/${vendorId}/rate-plans/${id}` : undefined;
          const tooltip = href ? `Open rate plan ${id}` : `Rate plan ${id} — vendor not yet resolved`;
          return (
            <LinkableChip
              {...getTagProps({ index })}
              key={id}
              label={known?.name ?? id}
              size="small"
              color={valid ? 'default' : 'error'}
              href={href}
              tooltip={tooltip}
            />
          );
        })
      }
      renderOption={(props, id) => {
        const opt = ratePlansById.get(id);
        return (
          <li {...props} key={id}>
            <Box display="flex" flexDirection="column">
              <Typography variant="body2">{opt?.name ?? id}</Typography>
              <Typography variant="caption" color="text.secondary">
                {id}
                {opt?.status && opt.status !== 'content-approved' ? ` · ${opt.status}` : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          helperText={helperText}
          error={invalid.length > 0}
          onFocus={() => setHasInteracted(true)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default BundleLpcRatePlanIdsPicker;
